/**
 * Formats repetitive status reasons by grouping common patterns
 * @param {string} reason - The raw reason string from API
 * @returns {string} - Formatted reason string
 */
export const formatReason = (reason) => {
  if (!reason || typeof reason !== "string") {
    return "Not Available";
  }

  // Split by semicolon and filter out empty strings
  const reasons = reason
    .split("\n")
    .filter((r) => r.trim())
    .map((r) => r.trim());

  if (reasons.length === 0) {
    return "Not Available";
  }

  if (reasons.length === 1) {
    return reasons[0];
  }

  // Check if all reasons are identical
  const firstReason = reasons[0];
  const allIdentical = reasons.every((r) => r === firstReason);

  if (allIdentical) {
    return `${firstReason} (${reasons.length})`;
  }

  // Group by common patterns
  const groupedReasons = groupReasonsByPattern(reasons);

  return formatGroupedReasons(groupedReasons);
};

/**
 * Groups reasons by common patterns
 * @param {string[]} reasons - Array of reason strings
 * @returns {Object} - Grouped reasons object
 */
const groupReasonsByPattern = (reasons) => {
  const groups = {};

  reasons.forEach((reason) => {
    // Try to find a common pattern
    const patternInfo = extractCommonPattern(reason);
    const key = patternInfo ? patternInfo.pattern : reason;

    if (!groups[key]) {
      groups[key] = {
        pattern: patternInfo ? patternInfo.pattern : null,
        patternType: patternInfo ? patternInfo.type : null,
        fullReasons: [],
        uniqueParts: new Set(),
        uniquePartsCount: {},
        multiUniqueParts: [], // For storing multiple unique parts
      };
    }

    groups[key].fullReasons.push(reason);

    if (patternInfo) {
      if (patternInfo.type === "multi_dynamic" && patternInfo.uniqueParts) {
        // Handle multi-part patterns
        const combinedKey = patternInfo.uniqueParts.join("|");
        groups[key].uniqueParts.add(combinedKey);
        groups[key].uniquePartsCount[combinedKey] =
          (groups[key].uniquePartsCount[combinedKey] || 0) + 1;

        // Store individual parts for better formatting
        if (
          !groups[key].multiUniqueParts.some(
            (existing) =>
              existing.join("|") === patternInfo.uniqueParts.join("|")
          )
        ) {
          groups[key].multiUniqueParts.push(patternInfo.uniqueParts);
        }
      } else {
        // Handle single unique part patterns
        groups[key].uniqueParts.add(patternInfo.uniquePart);
        groups[key].uniquePartsCount[patternInfo.uniquePart] =
          (groups[key].uniquePartsCount[patternInfo.uniquePart] || 0) + 1;
      }
    }
  });

  return groups;
};

/**
 * Extracts common pattern from a reason string using dynamic analysis
 * @param {string} reason - Reason string
 * @returns {Object|null} - Object with pattern and unique part or null
 */
const extractCommonPattern = (reason) => {
  // Try to find a dynamic pattern by analyzing the structure
  // Look for patterns like: "prefix unique_part suffix"

  // Split by common delimiters and analyze structure
  const words = reason.split(/\s+/);

  if (words.length < 3) {
    return null; // Too short to have a meaningful pattern
  }

  // Try to find a pattern where one word/phrase is unique and rest is common
  // This works for patterns like:
  // "AWS policy POLICY_NAME is attached DESCRIPTION"
  // "User USER_NAME has DESCRIPTION"
  // "Resource RESOURCE_NAME from subscription SUBSCRIPTION"
  // "Employee EMAIL has not yet accepted POLICY"
  // "Acceptance record found for POLICY by employee EMAIL on DATE"

  // Look for potential unique parts (usually names, IDs, emails, etc.)
  const potentialUniqueParts = [];

  for (let i = 0; i < words.length; i++) {
    const word = words[i];

    // Check if this word looks like a unique identifier
    // (contains numbers, special characters, or is camelCase/PascalCase)
    const isUniqueIdentifier =
      /[0-9]/.test(word) || // Contains numbers
      /[A-Z]/.test(word) || // Contains uppercase (camelCase/PascalCase)
      /[-_\.@]/.test(word) || // Contains special characters including @ for emails
      word.length > 10 || // Long words are often unique identifiers
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(word); // Email pattern

    if (isUniqueIdentifier) {
      potentialUniqueParts.push({
        index: i,
        word: word,
        confidence: calculateUniquenessConfidence(word),
      });
    }
  }

  // Sort by confidence and try the most likely unique parts
  potentialUniqueParts.sort((a, b) => b.confidence - a.confidence);

  // Try single unique part patterns first
  for (const uniquePart of potentialUniqueParts) {
    const pattern = createPatternFromUniquePart(words, uniquePart.index);
    if (pattern) {
      return {
        pattern: pattern.pattern,
        uniquePart: pattern.uniquePart,
        type: "dynamic",
        confidence: uniquePart.confidence,
      };
    }
  }

  // If no single unique part pattern found, try multiple unique parts
  if (potentialUniqueParts.length >= 2) {
    const multiPattern = createMultiUniquePartPattern(
      words,
      potentialUniqueParts
    );
    if (multiPattern) {
      return multiPattern;
    }
  }

  return null;
};

/**
 * Create a pattern with multiple unique parts
 * @param {string[]} words - Array of words
 * @param {Array} uniqueParts - Array of unique part objects
 * @returns {Object|null} - Pattern object or null
 */
const createMultiUniquePartPattern = (words, uniqueParts) => {
  try {
    // Create the pattern by replacing all unique parts with placeholders
    const patternWords = [...words];
    const uniquePartValues = [];

    // Sort by index to maintain order
    uniqueParts.sort((a, b) => a.index - b.index);

    // Replace unique parts with placeholders
    for (let i = 0; i < uniqueParts.length; i++) {
      const uniquePart = uniqueParts[i];
      const placeholder = `{unique_part_${i + 1}}`;
      patternWords[uniquePart.index] = placeholder;
      uniquePartValues.push(uniquePart.word);
    }

    const pattern = patternWords.join(" ");
    const combinedUniquePart = uniquePartValues.join("|"); // Use | as separator

    // Validate that this creates a meaningful pattern
    if (
      pattern.length < 10 ||
      uniquePartValues.some((part) => part.length < 2)
    ) {
      return null;
    }

    return {
      pattern: pattern,
      uniquePart: combinedUniquePart,
      type: "multi_dynamic",
      confidence:
        uniqueParts.reduce((sum, part) => sum + part.confidence, 0) /
        uniqueParts.length,
      uniqueParts: uniquePartValues,
    };
  } catch (error) {
    return null;
  }
};

/**
 * Calculate how likely a word is to be a unique identifier
 * @param {string} word - Word to analyze
 * @returns {number} - Confidence score (0-1)
 */
const calculateUniquenessConfidence = (word) => {
  let confidence = 0;

  // Contains numbers (high confidence)
  if (/[0-9]/.test(word)) confidence += 0.4;

  // Contains uppercase letters (medium confidence)
  if (/[A-Z]/.test(word)) confidence += 0.3;

  // Contains special characters (medium confidence)
  if (/[-_\.@]/.test(word)) confidence += 0.2;

  // Long word (low confidence)
  if (word.length > 10) confidence += 0.1;

  // Email pattern (very high confidence)
  if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(word)) {
    confidence += 0.8;
  }

  // Contains common words (reduce confidence)
  const commonWords = [
    "the",
    "and",
    "or",
    "is",
    "are",
    "was",
    "were",
    "has",
    "have",
    "had",
    "will",
    "can",
    "should",
    "must",
    "not",
    "no",
    "yes",
    "true",
    "false",
    "null",
    "undefined",
    "microsoft",
    "azure",
    "sponsorship",
    "subscription",
    "account",
    "storage",
    "policy",
    "user",
    "keyvault",
    "employee",
    "accepted",
    "acceptance",
    "record",
    "found",
    "yet",
    "has",
    "not",
    "by",
    "on",
    "for",
    "the",
    "to",
    "in",
    "at",
    "with",
    "from",
    "of",
    "a",
    "an",
    "this",
    "that",
    "these",
    "those",
  ];
  if (commonWords.includes(word.toLowerCase())) confidence -= 0.8;

  // Additional checks for better identification
  // Words that are likely to be resource names (high confidence)
  if (/^[a-z]+[a-z0-9]*[a-z0-9]$/i.test(word) && word.length >= 6) {
    // camelCase or kebab-case resource names
    confidence += 0.3;
  }

  // Words that are clearly not unique (very low confidence)
  if (
    /^(enabled|disabled|attached|from|to|in|on|at|by|for|with|without)$/i.test(
      word
    )
  ) {
    confidence -= 1.0;
  }

  return Math.max(0, Math.min(1, confidence));
};

/**
 * Create a pattern by replacing the unique part with a placeholder
 * @param {string[]} words - Array of words
 * @param {number} uniqueIndex - Index of the unique word
 * @returns {Object|null} - Pattern object or null
 */
const createPatternFromUniquePart = (words, uniqueIndex) => {
  try {
    // Create the pattern by replacing the unique part with a placeholder
    const patternWords = [...words];
    const uniquePart = patternWords[uniqueIndex];

    // Replace with placeholder
    patternWords[uniqueIndex] = "{unique_part}";

    const pattern = patternWords.join(" ");
    const uniquePartValue = uniquePart;

    // Validate that this creates a meaningful pattern
    if (pattern.length < 10 || uniquePartValue.length < 2) {
      return null;
    }

    return {
      pattern: pattern,
      uniquePart: uniquePartValue,
    };
  } catch (error) {
    return null;
  }
};

/**
 * Formats grouped reasons into readable output
 * @param {Object} groupedReasons - Grouped reasons object
 * @returns {string} - Formatted reason string
 */
const formatGroupedReasons = (groupedReasons) => {
  const formattedParts = [];

  Object.entries(groupedReasons).forEach(([key, group]) => {
    if (group.fullReasons.length === 1) {
      // Single occurrence, show as is
      formattedParts.push(group.fullReasons[0]);
    } else if (group.pattern) {
      // Multiple occurrences with pattern
      const uniquePartsArray = Array.from(group.uniqueParts);

      if (uniquePartsArray.length === 0) {
        // All identical
        formattedParts.push(
          `${group.fullReasons[0]} (${group.fullReasons.length})`
        );
      } else {
        // Handle multi-part patterns specifically
        if (
          group.patternType === "multi_dynamic" &&
          group.multiUniqueParts.length > 0
        ) {
          formattedParts.push(formatMultiPartPattern(group));
        } else {
          // Format the common pattern for single unique part
          const formattedPattern = group.pattern
            .replace("{policy_name}", "")
            .replace("{vault_name}", "")
            .replace("{user_name}", "")
            .replace("{resource_name}", "")
            .replace("{unique_part}", "");

          // Create the formatted output
          let formattedOutput =
            formattedPattern + ` (${group.fullReasons.length})`;

          // Always show all unique items with their counts
          const uniqueItemsWithCounts = uniquePartsArray.map((item) => {
            const count = group.uniquePartsCount[item];
            return count > 1 ? `${item} (${count})` : item;
          });

          // If there's only one unique item, show it inline
          if (uniquePartsArray.length === 1) {
            const singleItem = uniquePartsArray[0]; // Use the original item without count
            const count = group.uniquePartsCount[uniquePartsArray[0]];
            if (count === group.fullReasons.length) {
              // All occurrences are of the same unique item, show inline
              formattedOutput = formattedOutput.replace("", singleItem);
            } else {
              // Show as bullet point with count
              const itemWithCount =
                count > 1 ? `${singleItem} (${count})` : singleItem;
              formattedOutput += `\n• ${itemWithCount}`;
            }
          } else {
            // Multiple unique items, show as bullet points
            formattedOutput += `\n• ${uniqueItemsWithCounts.join("\n• ")}`;
          }
          formattedParts.push(formattedOutput);
        }
      }
    } else {
      // No pattern found, show count
      formattedParts.push(
        `${group.fullReasons[0]} (${group.fullReasons.length})`
      );
    }
  });

  return formattedParts.join("\n\n");
};

/**
 * Formats multi-part patterns with better readability
 * @param {Object} group - Group object with multi-part pattern data
 * @returns {string} - Formatted multi-part pattern string
 */
const formatMultiPartPattern = (group) => {
  const count = group.fullReasons.length;

  if (count === 1) {
    return group.fullReasons[0];
  }

  // Extract the common pattern structure
  let commonPattern = group.pattern;

  // Replace all unique part placeholders to get the common structure
  for (let i = 1; i <= 10; i++) {
    // Support up to 10 unique parts
    commonPattern = commonPattern.replace(`{unique_part_${i}}`, "");
  }

  // Clean up extra spaces
  commonPattern = commonPattern.replace(/\s+/g, " ").trim();

  // Create the formatted output
  let formattedOutput = `${commonPattern} (${count})`;

  // Show the unique combinations
  const uniqueCombinations = group.multiUniqueParts.map((parts) => {
    return parts.join(" | ");
  });

  if (uniqueCombinations.length === 1) {
    // All have the same combination, show inline
    formattedOutput = formattedOutput.replace("", uniqueCombinations[0]);
  } else {
    // Show as bullet points
    formattedOutput += `\n• ${uniqueCombinations.join("\n• ")}`;
  }

  return formattedOutput;
};

/**
 * Alternative simpler formatting for very long reasons
 * @param {string} reason - The raw reason string from API
 * @returns {string} - Simplified formatted reason string
 */
export const formatReasonSimple = (reason) => {
  if (!reason || typeof reason !== "string") {
    return "Not Available";
  }

  // Split by semicolon and filter out empty strings
  const reasons = reason
    .split(";")
    .filter((r) => r.trim())
    .map((r) => r.trim());

  if (reasons.length === 0) {
    return "Not Available";
  }

  if (reasons.length === 1) {
    return reasons[0];
  }

  // Check if all reasons are identical
  const firstReason = reasons[0];
  const allIdentical = reasons.every((r) => r === firstReason);

  if (allIdentical) {
    return `${firstReason} (${reasons.length})`;
  }

  // For different reasons, show first few and count
  const uniqueReasons = [...new Set(reasons)];

  if (uniqueReasons.length <= 3) {
    return uniqueReasons.join("; ");
  } else {
    return `${uniqueReasons.slice(0, 2).join("; ")}; ... and ${
      uniqueReasons.length - 2
    } more (${reasons.length} total)`;
  }
};
